import * as React from 'react';
import { useEffect, useState, useReducer } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import dayjs from 'dayjs';
import 'dayjs/locale/it';
import { ThemeProvider } from '@mui/material/styles';
import axios from 'axios';
import {
  Card,
  Container,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableContainer,
  TableFooter,
  Box,
  InputAdornment,
  TableCell,
  Tab,
  Tabs,
  TablePagination,
  TextField,
  Stack,
  DialogContent,
  DialogTitle,
  Dialog,
  Grid,
  Tooltip,
  Checkbox,
  FormControlLabel,
  Switch,
  DialogActions,
  Alert,
  MenuItem,
} from '@mui/material';
import { styled } from '@mui/system';
import useMediaQuery from '@mui/material/useMediaQuery';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Unstable_Popup as BasePopup } from '@mui/base/Unstable_Popup';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
//--------------------------------------------------------
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import GppGoodIcon from '@mui/icons-material/GppGood';
import GppBadIcon from '@mui/icons-material/GppBad';
import Lock from '@mui/icons-material/Https';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import PublicIcon from '@mui/icons-material/Public';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AddIcon from '@mui/icons-material/Add';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

//--------------------------------------------------------
import {
  PopupSuccessBody,
  PopupErrorBody,
  POPUP_DURATION_SUCCESS,
  POPUP_DURATION_ERROR,
} from '../../utils/popUpStyles';
import { CustomAutocomplete, CustomTextField, CustomDatePicker } from '../../utils/StyledInputs';
import { CustomIconButton, DefaultContainedButton, DefaultOutlinedButton, SearchIconButton } from '../../utils/Buttons';
import { CustomSnackbar, CreateSnackbarFunctions } from '../../utils/Snackbar';
import { ErrorUI, HttpErrorHandler } from '../../utils/ErrorHandler';
import { validatePhoneNumber } from '../../utils/numberValidity';
import { PaginationAPI, theme } from '../../utils/TableStyles';
import { LoadingUI } from '../../utils/LayoutStyles';
import { MembershipCardIcon } from '../../utils/Common';
import { EntitiesDialog } from '../../utils/EntitiesDialog';
import genderList from '../../model/gender';
import API from '../../api';

// Custom popup style
const Popup = styled(BasePopup)`
  z-index: 1400;
`;

dayjs.locale('it');

export default function Users() {
  // genaral
  const navigate = useNavigate();
  const is400Screen = useMediaQuery('(min-width: 400px)');
  const is600Screen = useMediaQuery('(min-width: 600px)');
  const [currentProfile, setCurrentProfile] = useState('');
  const [canCreateUser, setCanCreateUser] = useState(false);

  const [errors, setErrors] = useState({
    name: false,
    surname: false,
    birth_date: false,
    place_of_birth: false,
    gender: false,
    name_alias: false,
    home_country_id: false,
    home_city_id: false,
    home_city_name: false,
    home_province_id: false,
    home_region_id: false,
    home_region_name: false,
    home_street: false,
    home_zip_code: false,
    call_code: false,
    res_street: false,
    fiscal_code: false,
    res_country_id: false,
    res_city_id: false,
    res_city_name: false,
    res_province_id: false,
    res_region_id: false,
    res_region_name: false,
    entity_id: false,
    res_zip_code: false,
  });

  // snackbar handler
  const {
    open: openRegisterDataSnack,
    handleOpen: handleOpenRegisterDataSnack,
    handleClose: handleCloseRegisterDataSnack,
  } = CreateSnackbarFunctions(false);

  // Popup handler
  const [anchor, setAnchor] = useState(null);
  const [open, setOpen] = useState(false);
  const [popUpText, setPopUpText] = useState('');
  const [popupSuccess, setPopupSuccess] = useState('');

  // Show Popup Function
  const showPopup = (success, closeDialog, text) => {
    setPopupSuccess(success);
    setOpen(true);

    const totalDelay = success ? POPUP_DURATION_SUCCESS : POPUP_DURATION_ERROR;
    const animationDelay = 400;

    if (text) {
      setPopUpText(text);
    }

    setTimeout(() => {
      setOpen(false);

      setTimeout(() => {
        if (closeDialog) {
          handleCloseNewUser();
          getUsers();
        }
      }, animationDelay);
    }, totalDelay);
  };

  // Tabs handler
  const [value, setValue] = useState(0);

  // ---------------------------- User details section ---------------------------------

  // api static data
  const [users, setUsers] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  // api handler
  const [apiResponseData, setApiResponseData] = useState({});
  const [apiCalled, setApiCalled] = useState(false);
  const [rowsApiCalled, setRowsApiCalled] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // users applied filters
  const [advancedFilter, setAdvancedFilter] = useState(false);

  const [openEntitiesFilter, setOpenEntitiesFilter] = useState(false);

  const handleCloseEntitiesFilterDialog = () => {
    setOpenEntitiesFilter(false);
    setOpen(false);
  };

  const [entityFilter, setEntityFilter] = useState(null);
  const [activeEntityFilter, setActiveEntityFilter] = useState(false);

  const getEntityFilter = (selectedEntity) => {
    setEntityFilter(selectedEntity);
    setActiveEntityFilter(true);
  };

  // Activate Advanced Search Filters
  const toggleAdvancedSearchFilters = () => {
    if (advancedFilter) {
      setAdvancedFilter(!advancedFilter);
      removeFilters();
    } else {
      setAdvancedFilter(!advancedFilter);
    }
  };

  // disable all advanced filters
  const removeFilters = () => {
    setEntityFilter(null);
    setActiveEntityFilter(false);
  };

  const [searchedInput, setSearchedInput] = useState('');

  const handleChangeTab = (event, newValue) => {
    setValue(newValue);
    setSearchedInput('');
    setAdvancedFilter(false);
    removeFilters();
  };

  /* -------------------------------- New User -------------------------------- */
  const [domRes, setDomRes] = useState(true);
  const [birthDate, setBirthDate] = useState('');
  const [birthPlace, setBirthPlace] = useState('');
  const [birthPlacesList, setBirthPlacesList] = useState([]);
  const [sixteenYearsAgo, setXisteenYearsAgo] = useState(dayjs().subtract(16, 'year'));
  const [gender, setGender] = useState('');
  const [callCode, setCallCode] = useState(null);
  const [entity, setEntity] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [gdCheck, setGdCheck] = useState(false);
  const [isGdAvailable, setIsGdAvailable] = useState(false);
  const [entitiesFilterByResidence, setEntitiesFilterByResidence] = useState(true);
  const [disableEntity, setDisableEntity] = useState(false);

  // Residence
  const [resCountry, setResCountry] = useState('');
  const [resRegion, setResRegion] = useState('');
  const [resProvince, setResProvince] = useState('');
  const [resCity, setResCity] = useState('');
  const [resStreet, setResStreet] = useState('');
  const [resRegionName, setResRegionName] = useState(null);
  const [resCityName, setResCityName] = useState(null);

  // Domicile
  const [homeCountry, setHomeCountry] = useState('');
  const [homeRegion, setHomeRegion] = useState('');
  const [homeProvince, setHomeProvince] = useState('');
  const [homeCity, setHomeCity] = useState('');
  const [homeStreet, setHomeStreet] = useState('');
  const [homeRegionName, setHomeRegionName] = useState(null);
  const [homeCityName, setHomeCityName] = useState(null);

  // api static data
  const [resRegionList, setResRegionList] = useState([]);
  const [resCountriesList, setResCountriesList] = useState([]);
  const [resProvincesList, setResProvincesList] = useState([]);
  const [resCitiesList, setResCitiesList] = useState([]);
  const [homeRegionsList, setHomeRegionsList] = useState([]);
  const [homeCountriesList, setHomeCountriesList] = useState([]);
  const [homeProvincesList, setHomeProvincesList] = useState([]);
  const [homeCitiesList, setHomeCitiesList] = useState([]);
  const [PositionsList, setPositionsList] = useState([]);
  const [EntitiesList, setEntitiesList] = useState([]);
  const [emptyEntitiesList, setEmptyEntitiesList] = useState(false);
  const [italy, setItaly] = useState(null);

  const [onlineUser, setOnlineUser] = useState(false);

  const handleChangeOnlineUser = () => {
    setOnlineUser(!onlineUser);
  };

  const [userNewsletter, setUserNewsletter] = useState(true);

  const handleChangeUserNewsletter = () => {
    setUserNewsletter(!userNewsletter);
  };

  // dynamic containers
  const [homeDynamicContainer, setHomeDynamicContainer] = useState(0);
  const [aliasDynamicContainer, setAliasDynamicContainer] = useState(0);

  // Domicile Dynamic Container Show/Hide
  useEffect(() => {
    if (domRes) {
      setHomeDynamicContainer(0);
      setEntitiesFilterByResidence(true);
    } else {
      setHomeDynamicContainer(1);
    }
  }, [domRes]);

  // GD Check Status
  useEffect(() => {
    if (isGdAvailable) {
      setGdCheck(true);
    } else {
      setGdCheck(false);
    }
  }, [isGdAvailable]);

  // Membership GD Checkbox
  const handleGdCheck = () => {
    setGdCheck((current) => !current);
  };

  // Check If User Has Less Than 30 Years
  useEffect(() => {
    const thirtyYearsAgo = dayjs().subtract(30, 'year');
    const hasLessThan30Years = dayjs(birthDate).isAfter(dayjs(thirtyYearsAgo));

    setIsGdAvailable(hasLessThan30Years);
  }, [birthDate]);

  // register api status
  const [registerApiCalled, setRegisterApiCalled] = useState(false);

  // Date Select
  const handleDateChange = (date) => {
    const formattedDate = dayjs(date).format('YYYY/MM/DD');
    setBirthDate(formattedDate);
  };

  // Domicile = Residence checkbox
  const HandleDomRes = () => {
    setHomeRegion('');
    setHomeProvince('');
    setHomeCity('');
    setHomeStreet('');
    setDomRes((current) => !current);
  };

  // new user dialog handler
  const [openNewUser, setOpenNewUser] = useState(false);

  // get positions
  const getPositions = () => {
    API.get('/positions/registration')
      .then((response) => {
        setPositionsList(response.data);
      })
      .catch((error) => {
        HttpErrorHandler(error, setPopUpText);
        showPopup(false, false);
      });
  };

  // open new user dialog
  const handleOpenNewUser = () => {
    setOpenNewUser(true);

    if (callCode !== null && callCode !== undefined && callCode !== '') {
      setResCountry(callCode);
      setHomeCountry(callCode);
    }

    if (PositionsList.length === 0) {
      getPositions();
    }

    if (birthPlacesList.length === 0) {
      const fetchData = async () => {
        try {
          const [citiesResponse] = await axios.all([API.get('/citiesfiscalcode')]);

          setBirthPlacesList(citiesResponse.data);
        } catch (error) {
          HttpErrorHandler(error, setPopUpText);
          showPopup(false, false);
        }
      };

      fetchData();
    }

    if (resCountriesList.length === 0) {
      // get contries
      const fetchData = async () => {
        try {
          const [countriesResponse] = await axios.all([API.get('/countries')]);

          setResCountriesList(countriesResponse.data);
          setHomeCountriesList(countriesResponse.data);
          const italyData = countriesResponse.data.find((element) => element.name === 'Italia');

          setItaly(italyData);
          setResCountry(italyData);
          setHomeCountry(italyData);
          setCallCode(italyData);
          setRegisterApiCalled(true);
        } catch (error) {
          HttpErrorHandler(error, setPopUpText);
          showPopup(false, false);
        }
      };

      fetchData();
    }
  };

  // Get resRegionList
  useEffect(() => {
    if (resCountry !== null && resCountry !== undefined && resCountry !== '') {
      if (typeof resCountry === 'object' && Object.keys(resCountry).length > 0) {
        API.get(`/countries/regions`, {
          params: {
            id: resCountry.id,
          },
        }).then((response) => {
          if (response.data.length > 0) {
            setResRegionList(response.data);
          } else {
            setResRegionList([]);
            setResProvincesList([]);
            setResCitiesList([]);
          }
        });
      }
    }
  }, [resCountry]);

  // Get resProvincesList
  useEffect(() => {
    if (resRegion !== null && resRegion !== undefined && resRegion !== '') {
      if (typeof resRegion === 'object' && Object.keys(resRegion).length > 0) {
        API.get(`/regions/provinces`, {
          params: {
            region_name: resRegion.region_name,
          },
        }).then((response) => {
          if (response.data.length > 0) {
            setResProvincesList(response.data);
          } else {
            setResProvincesList([]);
            setResCitiesList([]);
          }
        });
      }
    }
  }, [resRegion]);

  // Get resCitiesList
  useEffect(() => {
    if (resProvince !== null && resProvince !== undefined && resProvince !== '') {
      if (typeof resProvince === 'object' && Object.keys(resProvince).length > 0) {
        API.get(`/provinces/cities`, {
          params: {
            id: resProvince.id,
          },
        }).then((response) => {
          if (response.data.length > 0) {
            setResCitiesList(response.data);
          } else {
            setResCitiesList([]);
          }
        });
      }
    }
  }, [resProvince]);

  // Get homeRegionList
  useEffect(() => {
    if (homeCountry !== null && homeCountry !== undefined && homeCountry !== '') {
      if (typeof homeCountry === 'object' && Object.keys(homeCountry).length > 0) {
        API.get(`/countries/regions`, {
          params: {
            id: homeCountry.id,
          },
        }).then((response) => {
          if (response.data.length > 0) {
            setHomeRegionsList(response.data);
          } else {
            setHomeRegionsList([]);
            setHomeProvincesList([]);
            setHomeCitiesList([]);
          }
        });
      }
    }
  }, [homeCountry]);

  // Get homeProvincesList
  useEffect(() => {
    if (homeRegion !== null && homeRegion !== undefined && homeRegion !== '') {
      if (typeof homeRegion === 'object' && Object.keys(homeRegion).length > 0) {
        API.get(`/regions/provinces`, {
          params: {
            region_name: homeRegion.region_name,
          },
        }).then((response) => {
          if (response.data.length > 0) {
            setHomeProvincesList(response.data);
          } else {
            setHomeProvincesList([]);
            setHomeCitiesList([]);
          }
        });
      }
    }
  }, [homeRegion]);

  // Get homeCitiesList
  useEffect(() => {
    if (homeProvince !== null && homeProvince !== undefined && homeProvince !== '') {
      if (typeof homeProvince === 'object' && Object.keys(homeProvince).length > 0) {
        API.get(`/provinces/cities`, {
          params: {
            id: homeProvince.id,
          },
        }).then((response) => {
          if (response.data.length > 0) {
            setHomeCitiesList(response.data);
          } else {
            setHomeCitiesList([]);
          }
        });
      }
    }
  }, [homeProvince]);

  useEffect(() => {
    setDisableEntity(true);
    if (
      entitiesFilterByResidence &&
      resCity !== null &&
      resCity !== undefined &&
      resCity !== '' &&
      resProvince !== null &&
      resProvince !== undefined &&
      resProvince !== ''
    ) {
      API.get(`/entities/registration?city_id=${resCity.id}&province_id=${resProvince.id}`).then((response) => {
        setEntitiesList(response.data.entities);
        setEmptyEntitiesList(response.data.empty);
        setDisableEntity(false);
      });
    } else if (
      entitiesFilterByResidence &&
      resCountry !== null &&
      resCountry !== undefined &&
      resCountry !== '' &&
      resCountry.id !== italy.id
    ) {
      API.get(`/entities/abroad_registration?id=${resCountry?.id}`)
        .then((response) => {
          setEntitiesList(response.data.entities);
          setEmptyEntitiesList(response.data.empty);
          setDisableEntity(false);
        })
        .catch((error) => {
          HttpErrorHandler(error, setPopUpText);
          showPopup(false, false);
        });
    } else if (
      !entitiesFilterByResidence &&
      homeCity !== null &&
      homeCity !== undefined &&
      homeCity !== '' &&
      homeProvince !== null &&
      homeProvince !== undefined &&
      homeProvince !== ''
    ) {
      API.get(`/entities/registration?city_id=${homeCity.id}&province_id=${homeProvince.id}`).then((response) => {
        setEntitiesList(response.data.entities);
        setEmptyEntitiesList(response.data.empty);
        setDisableEntity(false);
      });
    } else if (
      !entitiesFilterByResidence &&
      homeCountry !== null &&
      homeCountry !== undefined &&
      homeCountry !== '' &&
      homeCountry.id !== italy.id
    ) {
      API.get(`/entities/abroad_registration?id=${homeCountry?.id}`)
        .then((response) => {
          setEntitiesList(response.data.entities);
          setEmptyEntitiesList(response.data.empty);
          setDisableEntity(false);
        })
        .catch((error) => {
          HttpErrorHandler(error, setPopUpText);
          showPopup(false, false);
        });
    }
  }, [resCity, resProvince, homeCity, homeProvince, resCountry, homeCountry, entitiesFilterByResidence]);

  useEffect(() => {
    setEntity(null);
  }, [entitiesFilterByResidence]);

  // Alias
  const [useAlias, setUseAlias] = useState(false);

  // Alias Dynamic Container Show/Hide
  useEffect(() => {
    if (useAlias) {
      setAliasDynamicContainer(1);
    } else {
      setAliasDynamicContainer(0);
    }
  }, [useAlias]);

  const [alias, setAlias] = useState(null);

  // close new user dialog
  const handleCloseNewUser = () => {
    setOpenNewUser(false);
    setResCountry(null);
    setResRegion('');
    setResProvince('');
    setResCity('');
    setResStreet('');
    setHomeCountry('');
    setHomeRegion('');
    setHomeProvince('');
    setHomeCity('');
    setHomeStreet('');
    setEntity(null);
    setOpen(false);

    setErrors((prevErrors) =>
      Object.keys(prevErrors).reduce((acc, key) => {
        acc[key] = false;
        return acc;
      }, {})
    );
  };

  useEffect(() => {
    if (domRes) {
      setHomeCity(resCity);
      setHomeProvince(resProvince);
      setHomeRegion(resRegion);
      setHomeCountry(resCountry);
    }
  }, [domRes, resCity, resProvince, resRegion, resCountry]);

  // create new user
  const handleSubmitNewUser = (event, redirect = false) => {
    event.preventDefault();

    let data1;
    if (redirect) {
      const form = event.target.form;
      data1 = new FormData(form);
    } else {
      data1 = new FormData(event.currentTarget);
    }

    const genderValue = gender ? gender.gender.toLowerCase() : null;

    let userGender;

    if (genderValue === 'uomo') {
      userGender = 'male';
    } else if (genderValue === 'donna') {
      userGender = 'female';
    } else if (genderValue === 'altro') {
      userGender = 'other';
    } else {
      userGender = null;
    }

    const oData = {
      name: data1.get('name'),
      surname: data1.get('surname'),
      birth_date: birthDate,
      place_of_birth: birthPlace.id,
      res_equal_home: Number(domRes),
      home_country_id: domRes ? (resCountry ? resCountry.id : '') : homeCountry ? homeCountry.id : '',
      home_street: domRes ? resStreet : homeStreet,
      home_zip_code: domRes ? data1.get('res-cap') : data1.get('dom-cap'),
      call_code: data1.get('call_code'),
      allow_news: Number(userNewsletter),
      res_street: resStreet,
      fiscal_code: data1.get('fiscal-code').toUpperCase(),
      res_country_id: resCountry ? resCountry.id : '',
      entity_id: entity ? entity.id : '',
      user_online: Number(onlineUser),
      res_zip_code: data1.get('res-cap'),
      gd_check: Number(gdCheck),
    };

    if (resCountry !== null && resCountry !== undefined && resCountry !== '' && resCountry.id === italy.id) {
      oData.res_city_id = resCity ? resCity.id : '';
      oData.res_province_id = resProvince ? resProvince.id : '';
      oData.res_region_id = resRegion ? resRegion.region_name : '';

      if (domRes) {
        oData.home_city_id = resCity ? resCity.id : '';
        oData.home_province_id = resProvince ? resProvince.id : '';
        oData.home_region_id = resRegion ? resRegion.region_name : '';
      }
    } else {
      oData.res_city_name = resCityName || '';
      oData.res_region_name = resRegionName || '';

      if (domRes) {
        oData.home_city_name = resCityName || '';
        oData.home_region_name = resRegionName || '';
      }
    }

    if (!domRes) {
      if (homeCountry !== null && homeCountry !== undefined && homeCountry !== '' && homeCountry.id === italy.id) {
        oData.home_city_id = homeCity ? homeCity.id : '';
        oData.home_province_id = homeProvince ? homeProvince.id : '';
        oData.home_region_id = homeRegion ? homeRegion.region_name : '';
      } else {
        oData.home_city_name = homeCityName || '';
        oData.home_region_name = homeRegionName || '';
      }
    }

    if (useAlias) {
      oData.gender = userGender;
      oData.name_alias = alias;
    }

    let itemsLeft = false;
    const newErrors = { ...errors };

    Object.keys(oData).forEach((key) => {
      const value = oData[key];

      if (Object.prototype.hasOwnProperty.call(oData, key)) {
        if (
          (value === undefined ||
            value === null ||
            value === 'Invalid Date' ||
            (typeof value === 'string' && value.length === 0)) &&
          !(
            (key === 'home_country_id' && oData.res_equal_home) ||
            (key === 'home_region_id' && oData.res_equal_home) ||
            (key === 'home_province_id' && oData.res_equal_home) ||
            (key === 'home_city_id' && oData.res_equal_home) ||
            (key === 'home_street' && oData.res_equal_home) ||
            (key === 'home_zip_code' && oData.res_equal_home) ||
            key === 'gender'
          )
        ) {
          itemsLeft = true;
          if (Object.prototype.hasOwnProperty.call(newErrors, key)) {
            newErrors[key] = true;
          }
          handleOpenRegisterDataSnack();
        } else {
          newErrors[key] = false;
        }
      }
    });

    setErrors(newErrors);

    if (!itemsLeft) {
      delete oData.home_region_id;
      delete oData.res_region_id;

      if (data1.get('email').length > 0) {
        oData.email = data1.get('email').replace(/\s+/g, '');
      }

      if (phoneNumber.length > 0) {
        oData.phone = phoneNumber;
      } else {
        delete oData.call_code;
      }

      if (dayjs(birthDate).isBefore(dayjs(sixteenYearsAgo))) {
        API.post('/admin/users/offline', oData)
          .then((response) => {
            if (response.status === 201) {
              if (response.data !== '') {
                setPopUpText('Registrazione avvenuta con successo');
                showPopup(true, true);
                setResCountry(null);
                setResRegion('');
                setResProvince('');
                setResCity('');
                setResStreet('');
                setHomeCountry('');
                setHomeRegion('');
                setHomeProvince('');
                setHomeCity('');
                setHomeStreet('');
                setEntity('');
              }
            }

            const userId = response.data.id;
            if (redirect) {
              navigate('/admin/users/user-details', { state: { userID: userId } });
            }

            if (response.status === 205) {
              setPopUpText('Utente già registrato');
              showPopup(false, false);
            }
          })
          .catch((error) => {
            const errorMessage = getFirstApiError(error.response?.data);
            setPopUpText(errorMessage);
            showPopup(false, false);
          });
      } else {
        setPopUpText("L'utente deve avere compiuto almeno i 16 anni di età");
        showPopup(false, false);
      }
    }
  };

  const getFirstApiError = (errorResponse) => {
    if (errorResponse?.errors && Object.keys(errorResponse.errors).length > 0) {
      const firstError = Object.values(errorResponse.errors)[0][0];
      return firstError;
    }

    if (errorResponse?.message) {
      return errorResponse.message;
    }

    // Caso generico
    return 'Si è verificato un errore';
  };

  const [canVerifyUsers, setCanVerifyUsers] = useState(false);

  // get initial apis
  useEffect(() => {
    const savedFilters = sessionStorage.getItem('userFilters');
    if (savedFilters) {
      const filters = JSON.parse(savedFilters);
      Object.keys(filters).forEach((key) => {
        if (key === 'entity') {
          handleEntityChange(filters[key]);
        } else {
          handleFieldChange(key, filters[key]);
        }
      });
    }
    getUsers();

    const data = JSON.parse(localStorage.getItem('userData'));
    setCanVerifyUsers(data.can_verify_users);
  }, []);

  /* -------------------------------- Get Users ------------------------------- */

  const [formState, dispatchFormState] = useReducer(formReducer, {
    name: '',
    surname: '',
    email: '',
    phone: '',
    card_number: '',
    fiscal_code: '',
    entity: null,
    category: '',
  });

  const saveFiltersToSessionStorage = (state) => {
    sessionStorage.setItem('userFilters', JSON.stringify(state));
  };

  function formReducer(state, action) {
    switch (action.type) {
      case 'CHANGE_FIELD':
        return { ...state, [action.field]: action.value };
      case 'CHANGE_ENTITY':
        return { ...state, entity: action.value };
        case 'RESET_FILTERS':
      return {
        name: '',
        surname: '',
        email: '',
        phone: '',
        card_number: '',
        fiscal_code: '',
        entity: null,
        category: '',
      };
      default:
        return state;
    }
  }

  const handleFieldChange = (field, value) => {
    dispatchFormState({ type: 'CHANGE_FIELD', field, value });
  };

  const handleEntityChange = (value) => {
    dispatchFormState({ type: 'CHANGE_ENTITY', value });
  };

  const submitForm = (e) => {
    e.preventDefault();
    saveFiltersToSessionStorage(formState);
    setPage(0);
    sessionStorage.setItem('page', JSON.stringify(0));

    getUsers();
  };

  const handleResetFilters = (e) => {
    e.preventDefault();

    dispatchFormState({type: 'RESET_FILTERS'});
    sessionStorage.setItem('userFilters', JSON.stringify({
      name: '',
      surname: '',
      email: '',
      phone: '',
      card_number: '',
      fiscal_code: '',
      entity: null,
      category: '',
    }))
    sessionStorage.setItem('page', JSON.stringify(0));
    setPage(0)

    getUsers()
  }

  // get all users
  const getUsers = () => {
    let filters = formState;

    // Se ci sono filtri salvati, usali
    const savedFilters = sessionStorage.getItem('userFilters');
    const savedPage = Number(sessionStorage.getItem('page'));

    if (savedFilters) {
      filters = JSON.parse(savedFilters);
    }

    const queryString = Object.keys(filters)
      .filter((key) => (key === 'entity' ? filters[key] !== null : filters[key] && filters[key].length > 0))
      .map((key) => {
        if (key === 'entity') {
          return `entity_id=${filters[key].id}`;
        }
        return `${key}=${filters[key]}`;
      })
      .join('&');

    API.get(`/users?${queryString}&page=${savedPage + 1}`)
      .then((response) => {
        setApiResponseData(response.data);
        setTotalUsers(response.data.total);
        setCanCreateUser(response.data.auth_type === 'W' || response.data.auth_type === 'D');
        setUsers(response.data.data);
        setApiCalled(true);
        setRowsApiCalled(true);
      })
      .catch((error) => {
        setApiCalled(true);
        HttpErrorHandler(error, setErrorMessage);
      });
  };

  useEffect(() => {
    setUsers(apiResponseData.data);
  }, [apiResponseData]);

  const [userToToggleVerifiedInfo, setUserToToggleVerifiedInfo] = useState({
    user: {},
    isVerified: null,
  });
  const [openToggleVerified, setOpenToggleVerified] = useState(false);

  const openToggleVerifiedDialog = (user, isVerified) => {
    setOpenToggleVerified(true);
    setUserToToggleVerifiedInfo({
      user,
      isVerified,
    });
  };

  const closeToggleVerifiedDialog = () => {
    setOpenToggleVerified(false);
    setUserToToggleVerifiedInfo({
      user: {},
      isVerified: null,
    });
  };

  const handleToggleVerified = () => {
    const url = `/users/${userToToggleVerifiedInfo.user.id}/verified?val=${
      userToToggleVerifiedInfo.isVerified ? 0 : 1
    }`;

    API.patch(url).then((response) => {
      closeToggleVerifiedDialog();

      getUsers();
    });
  };

  /* ------------------------------- Pagination ------------------------------- */
  const savedPage = sessionStorage.getItem('page') || 0;
  const [page, setPage] = useState(Number(savedPage));
  const [rowsPerPage, setRowsPerPage] = useState(is600Screen ? 10 : 5);
  const [sliceStartDisplay, setSliceStartDisplay] = useState(0);
  const [sliceEndDisplay, setSliceEndDisplay] = useState(0);

  // empty rows number
  const emptyRows = users?.length
  ? page > 0
    ? rowsPerPage === 5 && page % 2 === 0
      ? Math.max(0, 1 * rowsPerPage - users.length)
      : rowsPerPage === 5 && page % 2 !== 0
      ? Math.max(0, 1 * rowsPerPage + 5 - users.length)
      : rowsPerPage === 10
      ? Math.max(0, 1 * rowsPerPage - users.length)
      : 0
    : 0
  : 0;

  // dynamically displays users number
  useEffect(() => {
    if (rowsPerPage === 10) {
      setSliceStartDisplay(0);
      setSliceEndDisplay(10);
    } else if (rowsPerPage === 5) {
      if (page % 2 === 0) {
        setSliceStartDisplay(0);
        setSliceEndDisplay(5);
      } else {
        setSliceStartDisplay(5);
        setSliceEndDisplay(10);
      }
    }
  }, [rowsPerPage, page]);

  // change page
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    sessionStorage.setItem('page', JSON.stringify(newPage));

    // getUsers(newPage)
  };

  // change rows per page
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    sessionStorage.setItem('page', JSON.stringify(0));
    getUsers();
    setRowsApiCalled(false);
  };

  // get user data
  useEffect(() => {
    let data;

    if (localStorage.getItem('userData') !== 'undefined') data = JSON.parse(localStorage.getItem('userData'));

    if (data) {
      const _currentProfile = data.roles.filter((role) => role.last_used);
      setCurrentProfile(_currentProfile);
    }
  }, []);

  if (currentProfile && currentProfile[0].prof_type_name !== 'ADMIN') return <Navigate to={{ pathname: '/' }} />;

  return (
    <>
      <Helmet>
        <title>Anagrafiche Utenti</title>
      </Helmet>

      <Container maxWidth="xl">
        <Stack direction="row" justifyContent="end" alignItems="center" sx={{ paddingBottom: '24px' }}>
          {apiCalled && errorMessage?.length === 0 && (
            <DefaultContainedButton
              onClick={handleOpenNewUser}
              customStyles={{ display: canCreateUser ? 'flex' : 'none' }}
              startIcon={<AddIcon />}
            >
              Nuovo Utente
            </DefaultContainedButton>
          )}

          {/* New User Dialog */}
          <Dialog fullWidth maxWidth="lg" open={openNewUser} onClose={() => {}}>
            <DialogTitle>Registrazione nuovo utente</DialogTitle>
            <DialogContent>
              {registerApiCalled ? (
                <form method="post" id="register-form" onSubmit={handleSubmitNewUser}>
                  <Box>
                    <Grid sx={{ paddingTop: '20px' }} justifyContent="center" alignItems="center" container spacing={3}>
                      {/* Name */}
                      <Grid item xs={12} md={6} lg={5}>
                        <CustomTextField error={errors.name} id="register-name" name="name" label="Nome" />
                      </Grid>

                      {/* Surname */}
                      <Grid item xs={12} md={6} lg={5}>
                        <CustomTextField error={errors.surname} id="register-surname" name="surname" label="Cognome" />
                      </Grid>

                      <Grid item xs={12} lg={10}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={useAlias}
                              onChange={(e) => {
                                setUseAlias(e.target.checked);
                              }}
                            />
                          }
                          label="Utilizza nome alias"
                        />
                      </Grid>

                      {useAlias && (
                        <Grid item xs={12} lg={10}>
                          <Grid
                            container
                            spacing={3}
                            style={{ opacity: aliasDynamicContainer, transition: 'opacity .5s' }}
                          >
                            <Grid item xs={8}>
                              <CustomTextField
                                error={errors.name_alias}
                                id="register-alias"
                                name="alias"
                                label="Alias"
                                onChange={(e) => setAlias(e.currentTarget.value)}
                              />
                            </Grid>

                            {/* Gender */}
                            <Grid item xs={4}>
                              <CustomAutocomplete
                                required={useAlias}
                                id="register-gender"
                                options={genderList}
                                isOptionEqualToValue={(option, value) => option.gender === value.gender}
                                getOptionLabel={(option) => option.gender}
                                renderOption={(props, option) => (
                                  <Box component="li" {...props} key={option.gender}>
                                    {option.gender}
                                  </Box>
                                )}
                                onChange={(e, value) => {
                                  setGender(value);
                                }}
                                renderInput={(params) => (
                                  <TextField {...params} label="Sesso" name="gender" variant="outlined" />
                                )}
                              />
                            </Grid>

                            <Grid item xs={12}>
                              <Alert severity="warning">
                                Specificare un "Nome Alias" e il "Sesso" solo se si è in una fase di transizione di
                                genere.
                              </Alert>
                            </Grid>
                          </Grid>
                        </Grid>
                      )}

                      {/* Date of Birth */}
                      <Grid item xs={12} md={6} lg={5}>
                        <Box>
                          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="it">
                            <CustomDatePicker
                              error={errors.birth_date}
                              label="Data di Nascita*"
                              onChange={handleDateChange}
                              format="DD/MM/YYYY"
                              maxDate={sixteenYearsAgo}
                            />
                          </LocalizationProvider>
                        </Box>
                      </Grid>

                      <Grid item xs={12} md={6} lg={5}>
                        <CustomAutocomplete
                          options={birthPlacesList}
                          isOptionEqualToValue={(option, value) => option.id === value.id}
                          id="register-birthPlace"
                          getOptionLabel={(option) => option.name}
                          renderOption={(props, option) => (
                            <Box component="li" {...props} key={option.id}>
                              {option.province_abbr.length > 0
                                ? `${option.name} (${option.province_abbr})`
                                : option.name}
                            </Box>
                          )}
                          onChange={(e, value) => setBirthPlace(value)}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              error={errors.place_of_birth}
                              label="Luogo di Nascita *"
                              variant="outlined"
                            />
                          )}
                        />
                      </Grid>

                      <Grid item xs={12} lg={10}>
                        <Alert severity="warning">
                          Attenzione! Verrà verificata la compatibilità del codice fiscale con i tuoi dati anagrafici.
                          Se riscontri problemi contatta l'helpdesk all'indirizzo tesseramento@partitodemocratico.it
                        </Alert>
                      </Grid>

                      {/* Tax ID */}
                      <Grid item xs={12} md={6} lg={4}>
                        <CustomTextField
                          error={errors.fiscal_code}
                          id="register-fiscal-code"
                          name="fiscal-code"
                          label="Codice Fiscale"
                        />
                      </Grid>

                      {/* Email */}
                      <Grid item xs={12} md={6} lg={4}>
                        <CustomTextField
                          id="register-email"
                          name="email"
                          label="Email"
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <Tooltip arrow title="Riceverai un codice di verifica" placement="top">
                                  <InfoOutlinedIcon sx={{ color: '#2f58ca' }} />
                                </Tooltip>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>

                      {/* Call Code */}
                      <Grid item xs={4} md={3} lg={2}>
                        <CustomAutocomplete
                          options={resCountriesList}
                          id="register-phone-prefix"
                          getOptionLabel={(option) => option.call_code}
                          renderOption={(props, option) => (
                            <Box component="li" {...props} key={option.id}>
                              {option.name} {option.call_code}
                            </Box>
                          )}
                          value={callCode}
                          filterOptions={(options, state) => {
                            const newOptions = [];
                            options.forEach((element) => {
                              if (
                                element.name.toLowerCase().includes(state.inputValue.toLowerCase()) ||
                                element.call_code.toLowerCase().includes(state.inputValue.toLowerCase())
                              ) {
                                newOptions.push(element);
                              }
                            });
                            return newOptions;
                          }}
                          isOptionEqualToValue={(option, value) => callCode && option.id === value.id}
                          onChange={(e, value) => setCallCode(value)}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              error={errors.call_code}
                              label="Prefisso"
                              name="call_code"
                              variant="outlined"
                            />
                          )}
                        />
                      </Grid>

                      {/* Phone Number */}
                      <Grid item xs={8} md={5} lg={4}>
                        <CustomTextField
                          type="tel"
                          id="register-phone-number"
                          name="phone-number"
                          label="Cellulare"
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <Tooltip arrow title="Riceverai un codice di verifica" placement="top">
                                  <InfoOutlinedIcon sx={{ color: '#2f58ca' }} />
                                </Tooltip>
                              </InputAdornment>
                            ),
                          }}
                          onChange={(event) => validatePhoneNumber(event, setPhoneNumber)}
                        />
                      </Grid>

                      {/* Country */}
                      <Grid item xs={12} md={4} lg={3}>
                        <CustomAutocomplete
                          required
                          options={resCountriesList.filter(
                            (country) =>
                              (!resRegion || country.id === resRegion.country_id) &&
                              (!resProvince || country.id === resProvince.country_id)
                          )}
                          isOptionEqualToValue={(option, value) => option.id === value.id}
                          getOptionLabel={(option) => option.name}
                          id="register-res-country"
                          renderOption={(props, option) => (
                            <Box component="li" {...props} key={option.id}>
                              {option.name}
                            </Box>
                          )}
                          value={resCountry}
                          onChange={(e, value) => setResCountry(value)}
                          renderInput={(params) => (
                            <TextField {...params} error={errors.res_country_id} label="Paese *" variant="outlined" />
                          )}
                        />
                      </Grid>

                      {/* Region */}
                      <Grid item xs={12} md={6} lg={3}>
                        <CustomAutocomplete
                          freeSolo
                          forcePopupIcon={resCountry === italy}
                          options={resRegionList.filter(
                            (region) =>
                              (!resProvince || region.region_name === resProvince.region_name) &&
                              (!resCountry || region.country_id === resCountry.id)
                          )}
                          isOptionEqualToValue={(option, value) => option.region_name === value.region_name}
                          getOptionLabel={(option) => option.region_name}
                          id="register-res-region"
                          renderOption={(props, option) => (
                            <Box component="li" {...props} key={option.region_name}>
                              {option.region_name}
                            </Box>
                          )}
                          onChange={(e, value) => setResRegion(value)}
                          onInputChange={(e, value) => {
                            setResRegionName(value);
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              error={errors.res_region_id || errors.res_region_name}
                              label="Regione *"
                              variant="outlined"
                            />
                          )}
                        />
                      </Grid>

                      {/* Province */}
                      <Grid item xs={12} md={6} lg={3}>
                        <CustomAutocomplete
                          required
                          freeSolo
                          forcePopupIcon={resCountry === italy}
                          options={resProvincesList.filter(
                            (province) =>
                              (!resRegion || province.region_name === resRegion.region_name) &&
                              (!resCity || province.id === resCity.province_id) &&
                              (!resCountry || province.country_id === resCountry.id)
                          )}
                          isOptionEqualToValue={(option, value) => option.id === value.id}
                          id="register-res-province"
                          getOptionLabel={(option) => option.name}
                          renderOption={(props, option) => (
                            <Box component="li" {...props} key={option.id}>
                              {option.name}
                            </Box>
                          )}
                          onChange={(e, value) => setResProvince(value)}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              error={errors.res_province_id}
                              label="Provincia *"
                              variant="outlined"
                            />
                          )}
                        />
                      </Grid>

                      {/* City */}
                      <Grid item xs={12} md={6} lg={3}>
                        <CustomAutocomplete
                          required
                          freeSolo
                          forcePopupIcon={resCountry === italy}
                          options={resCitiesList.filter((city) => !resProvince || city.province_id === resProvince.id)}
                          isOptionEqualToValue={(option, value) => option.id === value.id}
                          id="register-res-city"
                          getOptionLabel={(option) => option.name}
                          renderOption={(props, option) => (
                            <Box component="li" {...props} key={option.id}>
                              {option.name}
                            </Box>
                          )}
                          onChange={(e, value) => setResCity(value)}
                          onInputChange={(e, value) => {
                            setResCityName(value);
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              error={errors.res_city_id || errors.res_city_name}
                              label="Comune *"
                              variant="outlined"
                            />
                          )}
                        />
                      </Grid>

                      {/* Street */}
                      <Grid item xs={12} md={6} lg={4}>
                        <CustomTextField
                          error={errors.res_street}
                          id="register-res-address"
                          name="res-adress"
                          onChange={(e) => {
                            setResStreet(e.target.value);
                          }}
                          label="Indirizzo"
                        />
                      </Grid>

                      {/* Zip Code */}
                      <Grid item xs={12} md={6} lg={3}>
                        <CustomTextField error={errors.res_zip_code} id="register-res-cap" name="res-cap" label="CAP" />
                      </Grid>

                      {/* Entity */}
                      <Grid item xs={12} md={6} lg={7}>
                        <CustomAutocomplete
                          id="register-circolo"
                          disabled={disableEntity}
                          options={EntitiesList}
                          value={entity}
                          isOptionEqualToValue={(option, value) => option.id === value.id}
                          getOptionLabel={(option) => option.name}
                          renderOption={(props, option) => (
                            <Box component="li" {...props} key={option.id}>
                              {option.name}
                            </Box>
                          )}
                          onChange={(e, value) => {
                            setEntity(value);
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              error={errors.entity_id}
                              label="Circolo di Appartenenza *"
                              variant="outlined"
                            />
                          )}
                        />
                      </Grid>

                      {emptyEntitiesList && (
                        <Grid item xs={12} lg={10}>
                          <Alert severity="error">Attenzione! Nessuna entità disponibile per la registrazione</Alert>
                        </Grid>
                      )}

                      <Grid item xs={12} lg={10}>
                        <Grid container spaging={3}>
                          <Grid item xs={12} md={4}>
                            <FormControlLabel
                              sx={{ paddingTop: '12px' }}
                              control={<Switch checked={Boolean(onlineUser)} onClick={handleChangeOnlineUser} />}
                              label={onlineUser ? 'Utente online' : 'Utente offline'}
                            />
                          </Grid>

                          <Grid item xs={12} md={4}>
                            <FormControlLabel
                              sx={{ paddingTop: '12px' }}
                              control={
                                <Switch checked={Boolean(userNewsletter)} onClick={handleChangeUserNewsletter} />
                              }
                              label={userNewsletter ? 'Riceve comunicazioni' : 'Non riceve comunicazioni'}
                            />
                          </Grid>
                        </Grid>
                      </Grid>

                      <Grid item xs={12}>
                        {/* Checkboxes Section + Dynamic Containers */}
                        <Stack direction="row">
                          <Grid justifyContent="center" alignItems="center" container spacing={2}>
                            <Grid item xs={12} lg={10}>
                              <FormControlLabel
                                control={<Checkbox checked={domRes} onChange={HandleDomRes} />}
                                label="Il domicilio corrisponde con la residenza"
                              />
                            </Grid>

                            <Grid item xs={12} lg={10} sx={{ display: domRes ? 'none' : 'flex' }}>
                              <FormControlLabel
                                control={
                                  <Switch
                                    checked={entitiesFilterByResidence}
                                    onChange={() => setEntitiesFilterByResidence(!entitiesFilterByResidence)}
                                    disabled={domRes}
                                  />
                                }
                                label={
                                  entitiesFilterByResidence
                                    ? 'Filtra i circoli rispetto alla residenza'
                                    : 'Filtra i circoli rispetto al domicilio'
                                }
                              />
                            </Grid>

                            {/* Domicile != Residence */}
                            {domRes === false && (
                              <Grid style={{ opacity: homeDynamicContainer, transition: 'opacity 1s' }} item xs={12}>
                                <Grid container justifyContent="center" alignItems="center" spacing={3}>
                                  <Grid item xs={12} lg={10}>
                                    <Typography sx={{ fontWeight: '500', textDecoration: 'underline' }}>
                                      Inserisci di seguito le informazioni relative al domicilio
                                    </Typography>
                                  </Grid>

                                  {/* Domicile Country */}
                                  <Grid item xs={12} md={4} lg={3}>
                                    <CustomAutocomplete
                                      options={homeCountriesList.filter(
                                        (country) =>
                                          (!homeRegion || country.id === homeRegion.country_id) &&
                                          (!homeProvince || country.id === homeProvince.country_id)
                                      )}
                                      getOptionLabel={(option) => option.name}
                                      isOptionEqualToValue={(option, value) => option.id === value.id}
                                      id="register-dom-country"
                                      renderOption={(props, option) => (
                                        <Box component="li" id="country-id" {...props} key={option.id}>
                                          {option.name}
                                        </Box>
                                      )}
                                      value={homeCountry}
                                      onChange={(e, value) => setHomeCountry(value)}
                                      renderInput={(params) => (
                                        <TextField
                                          {...params}
                                          error={errors.home_country_id}
                                          label="Paese"
                                          variant="outlined"
                                        />
                                      )}
                                    />
                                  </Grid>

                                  {/* Domicile Region */}
                                  <Grid item xs={12} md={4} lg={3}>
                                    <CustomAutocomplete
                                      options={homeRegionsList.filter(
                                        (region) =>
                                          (!homeProvince || region.region_name === homeProvince.region_name) &&
                                          (!homeCountry || region.country_id === homeCountry.id)
                                      )}
                                      freeSolo
                                      forcePopupIcon={homeCountry === italy}
                                      isOptionEqualToValue={(option, value) => option.region_name === value.region_name}
                                      getOptionLabel={(option) => option.region_name}
                                      id="register-dom-region"
                                      renderOption={(props, option) => (
                                        <Box component="li" {...props} key={option.region_name}>
                                          {option.region_name}
                                        </Box>
                                      )}
                                      onChange={(e, value) => setHomeRegion(value)}
                                      onInputChange={(e, value) => {
                                        setHomeRegionName(value);
                                      }}
                                      renderInput={(params) => (
                                        <TextField
                                          {...params}
                                          error={errors.home_region_id || errors.home_region_name}
                                          label="Regione"
                                          variant="outlined"
                                        />
                                      )}
                                    />
                                  </Grid>

                                  {/* Domicile Province */}
                                  <Grid item xs={12} md={4} lg={4}>
                                    <CustomAutocomplete
                                      options={homeProvincesList.filter(
                                        (province) =>
                                          (!homeRegion || province.region_name === homeRegion.region_name) &&
                                          (!homeCity || province.id === homeCity.province_id) &&
                                          (!homeCountry || province.country_id === homeCountry.id)
                                      )}
                                      freeSolo
                                      forcePopupIcon={homeCountry === italy}
                                      isOptionEqualToValue={(option, value) => option.id === value.id}
                                      id="register-dom-province"
                                      getOptionLabel={(option) => option.name}
                                      renderOption={(props, option) => (
                                        <Box component="li" {...props} key={option.id}>
                                          {option.name}
                                        </Box>
                                      )}
                                      onChange={(e, value) => setHomeProvince(value)}
                                      renderInput={(params) => (
                                        <TextField
                                          {...params}
                                          error={errors.home_province_id}
                                          label="Provincia"
                                          variant="outlined"
                                        />
                                      )}
                                    />
                                  </Grid>

                                  {/* Domicile City */}
                                  <Grid item xs={12} md={5} lg={4}>
                                    <CustomAutocomplete
                                      options={homeCitiesList.filter((city) =>
                                        homeProvince ? city.province_id === homeProvince.id : true
                                      )}
                                      freeSolo
                                      forcePopupIcon={homeCountry === italy}
                                      isOptionEqualToValue={(option, value) => option.id === value.id}
                                      id="register-dom-city"
                                      getOptionLabel={(option) => option.name}
                                      renderOption={(props, option) => (
                                        <Box component="li" {...props} key={option.id}>
                                          {option.name}
                                        </Box>
                                      )}
                                      onChange={(e, value) => setHomeCity(value)}
                                      onInputChange={(e, value) => {
                                        setHomeCityName(value);
                                      }}
                                      renderInput={(params) => (
                                        <TextField
                                          {...params}
                                          error={errors.home_city_id || errors.home_city_name}
                                          label="Comune"
                                          variant="outlined"
                                        />
                                      )}
                                    />
                                  </Grid>

                                  {/* Domicile Street */}
                                  <Grid item xs={12} md={5} lg={4}>
                                    <CustomTextField
                                      id="register-dom-adress"
                                      onChange={(e) => {
                                        setHomeStreet(e.target.value);
                                      }}
                                      name="dom-adress"
                                      label="Indirizzo"
                                      error={errors.home_street}
                                    />
                                  </Grid>

                                  {/* Domicile Zip Code */}
                                  <Grid item xs={12} md={2}>
                                    <CustomTextField
                                      error={errors.home_zip_code}
                                      id="register-dom-cap"
                                      name="dom-cap"
                                      label="CAP"
                                    />
                                  </Grid>
                                </Grid>
                              </Grid>
                            )}
                          </Grid>
                        </Stack>
                      </Grid>

                      <Grid item xs={12} lg={10}>
                        <FormControlLabel
                          control={<Checkbox disabled={!isGdAvailable} checked={gdCheck} onChange={handleGdCheck} />}
                          label="Iscrizione ai Giovani Democratici"
                          labelPlacement="end"
                        />
                      </Grid>
                    </Grid>
                  </Box>

                  {/* Submit Step1 */}
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="end"
                    gap="12px"
                    id="register-submit-button"
                  >
                    <DefaultOutlinedButton onClick={handleCloseNewUser}>Annulla</DefaultOutlinedButton>
                    <DefaultContainedButton type="submit" disabled={emptyEntitiesList}>
                      Crea
                    </DefaultContainedButton>

                    <DefaultContainedButton
                      type="submit"
                      onClick={(event) => handleSubmitNewUser(event, true)}
                      disabled={emptyEntitiesList}
                    >
                      Crea e vai alla scheda
                    </DefaultContainedButton>

                    <CustomSnackbar
                      open={openRegisterDataSnack}
                      handleClose={handleCloseRegisterDataSnack}
                      message="Inserisci i dati mancanti!"
                    />
                  </Stack>
                </form>
              ) : (
                LoadingUI()
              )}
              {!registerApiCalled && (
                <DialogActions>
                  <DefaultOutlinedButton onClick={handleCloseNewUser}>Chiudi</DefaultOutlinedButton>
                </DialogActions>
              )}
            </DialogContent>

            <Container ref={setAnchor}>
              <Popup anchor={anchor} open={open}>
                {popupSuccess ? (
                  <PopupSuccessBody sx={{ whiteSpace: 'pre-line' }}>{popUpText}</PopupSuccessBody>
                ) : (
                  <PopupErrorBody>{popUpText}</PopupErrorBody>
                )}
              </Popup>
            </Container>
          </Dialog>
        </Stack>
        {apiCalled ? (
          errorMessage?.length === 0 ? (
            <>
              <Card sx={{ padding: '24px', marginBottom: '12px' }}>
                <form onSubmit={submitForm}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={4}>
                      <CustomTextField
                        id="name"
                        label="Nome"
                        value={formState.name}
                        onChange={(e) => handleFieldChange('name', e.target.value)}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                      <CustomTextField
                        id="surname"
                        label="Cognome"
                        value={formState.surname}
                        onChange={(e) => handleFieldChange('surname', e.target.value)}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                      <CustomTextField
                        id="fiscal_code"
                        label="Codice Fiscale"
                        value={formState.fiscal_code}
                        onChange={(e) => handleFieldChange('fiscal_code', e.target.value)}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                      <CustomTextField
                        id="email"
                        label="Email"
                        value={formState.email}
                        onChange={(e) => handleFieldChange('email', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <CustomTextField
                        id="phone"
                        label="Telefono"
                        value={formState.phone}
                        onChange={(e) => handleFieldChange('phone', e.target.value)}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                      <CustomTextField
                        id="card_number"
                        label="Numero Tessera"
                        value={formState.card_number}
                        onChange={(e) => handleFieldChange('card_number', e.target.value)}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6} md={4} sx={{ alignSelf: 'start' }}>
                      <CustomTextField
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end" sx={{ cursor: 'pointer' }}>
                              {formState.entity ? (
                                <Tooltip title="Elimina" placement="top" arrow>
                                  <DeleteIcon
                                    onClick={() => {
                                      handleEntityChange(null);
                                    }}
                                  />
                                </Tooltip>
                              ) : (
                                <Tooltip title="Modifica" placement="top" arrow>
                                  <EditIcon
                                    onClick={() => {
                                      setOpenEntitiesFilter(!openEntitiesFilter);
                                    }}
                                  />
                                </Tooltip>
                              )}
                            </InputAdornment>
                          ),
                        }}
                        value={formState.entity ? formState.entity?.name : ''}
                        label="Entità"
                      />

                      <EntitiesDialog
                        openDialog={openEntitiesFilter}
                        closeDialog={handleCloseEntitiesFilterDialog}
                        outputEntity={handleEntityChange}
                        context={'users'}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                      <CustomTextField
                        id="category"
                        label="Categoria"
                        select
                        value={formState.category}
                        onChange={(e) => handleFieldChange('category', e.target.value)}
                      >
                        <MenuItem value="">Nessuna Categoria</MenuItem>
                        <MenuItem value="member">Tesserati</MenuItem>
                        <MenuItem value="verified">Verificati</MenuItem>
                        <MenuItem value="unverified">Non Verificati</MenuItem>
                        <MenuItem value="online">Online</MenuItem>
                        <MenuItem value="offline">Offline</MenuItem>
                        <MenuItem value="blocked">Bloccati</MenuItem>
                      </CustomTextField>
                    </Grid>

                    <Grid item xs={12} sm={6} md={2}>
                      <DefaultOutlinedButton
                        onClick={handleResetFilters}
                        customStyles={{ width: '100%', height: '100%', backgroundColor: '#f4f6f8' }}
                      >
                        Svuota Filtri
                      </DefaultOutlinedButton>
                    </Grid>

                    <Grid item xs={12} sm={6} md={2}>
                      <DefaultContainedButton
                        type="submit"
                        customStyles={{ width: '100%', height: '100%' }}
                      >
                        Cerca
                      </DefaultContainedButton>
                    </Grid>

                  </Grid>
                </form>
              </Card>

              <Card sx={{ padding: '24px' }}>
                <Dialog open={openToggleVerified} onClose={() => {}} fullWidth>
                  <DialogTitle>Verifica Utente</DialogTitle>
                  <DialogContent>
                    {userToToggleVerifiedInfo.isVerified
                      ? `Sei sicuro di voler rimuovere lo stato di utente verificato a ${userToToggleVerifiedInfo.user.name} ${userToToggleVerifiedInfo.user.surname}?`
                      : `Sei sicuro di voler verificare l'utente ${userToToggleVerifiedInfo.user.name} ${userToToggleVerifiedInfo.user.surname}?`}
                  </DialogContent>
                  <DialogActions>
                    <DefaultOutlinedButton onClick={closeToggleVerifiedDialog}>Chiudi</DefaultOutlinedButton>
                    <DefaultContainedButton onClick={handleToggleVerified}>Conferma</DefaultContainedButton>
                  </DialogActions>
                </Dialog>

                {/* Table */}
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Nome Utente</TableCell>
                        <TableCell>Codice Fiscale</TableCell>
                        {is400Screen && (
                          <>
                            <TableCell>Data di Nascita</TableCell>
                            <TableCell>Circolo</TableCell>
                          </>
                        )}

                        {is600Screen && (
                          <>
                            <TableCell align="center">Verificato</TableCell>
                            <TableCell align="center">Tesserato</TableCell>
                            <TableCell align="center">Online</TableCell>
                            <TableCell align="center">Attivo</TableCell>
                          </>
                        )}

                        <TableCell />
                      </TableRow>
                    </TableHead>

                    {users?.length > 0 ? (
                      <TableBody>
                        {rowsApiCalled ? (
                          users.slice(sliceStartDisplay, sliceEndDisplay).map((user) => {
                            return (
                              <TableRow hover key={user.id}>
                                <TableCell>
                                  {user.name_alias
                                    ? `${user.name_alias} [${user.name} ${user.surname}]`
                                    : `${user.name} ${user.surname}`}
                                </TableCell>
                                <TableCell>{user.fiscal_code}</TableCell>
                                {is400Screen && (
                                  <>
                                    <TableCell>{dayjs(user.birth_date).format('DD/MM/YYYY')}</TableCell>
                                    <TableCell>{user.master_entity[0].name}</TableCell>
                                  </>
                                )}

                                {is600Screen && (
                                  <>
                                    <TableCell align="center">
                                      {user.verified ? (
                                        canVerifyUsers ? (
                                          <Box>
                                            <Tooltip title="Clicca per rimuovere verifica" arrow placement="right">
                                              <Box>
                                                <CustomIconButton onClick={() => openToggleVerifiedDialog(user, true)}>
                                                  <GppGoodIcon sx={{ color: '#47ba25' }} />
                                                </CustomIconButton>
                                              </Box>
                                            </Tooltip>
                                          </Box>
                                        ) : (
                                          <GppGoodIcon sx={{ color: '#47ba25' }} />
                                        )
                                      ) : canVerifyUsers ? (
                                        <Box>
                                          <Tooltip title="Clicca per verificare" arrow placement="right">
                                            <Box>
                                              <CustomIconButton onClick={() => openToggleVerifiedDialog(user, false)}>
                                                <GppBadIcon sx={{ color: 'red' }} />
                                              </CustomIconButton>
                                            </Box>
                                          </Tooltip>
                                        </Box>
                                      ) : (
                                        <GppBadIcon sx={{ color: 'red' }} />
                                      )}
                                    </TableCell>
                                    <TableCell align="center">
                                      <MembershipCardIcon data={user} />
                                    </TableCell>
                                    <TableCell align="center">
                                      {user.user_online ? (
                                        <PublicIcon sx={{ color: '#47ba25' }} />
                                      ) : (
                                        <PublicIcon sx={{ color: '#9fa1a2' }} />
                                      )}
                                    </TableCell>
                                    <TableCell align="center">
                                      {user.user_status ? (
                                        <Lock sx={{ color: 'red' }} />
                                      ) : (
                                        <LockOpenIcon sx={{ color: '#47ba25' }} />
                                      )}
                                    </TableCell>
                                  </>
                                )}
                                <TableCell>
                                  <CustomIconButton
                                    onClick={() => {
                                      navigate('/admin/users/user-details', { state: { userID: user.id } });
                                    }}
                                  >
                                    <KeyboardArrowRightIcon />
                                  </CustomIconButton>
                                </TableCell>
                              </TableRow>
                            );
                          })
                        ) : (
                          <TableRow>
                            <TableCell colSpan={9}>{LoadingUI()}</TableCell>
                          </TableRow>
                        )}

                        {/* Empty Rows */}
                        {emptyRows > 0 && apiCalled && (
                          <TableRow style={{ height: 53 * emptyRows }}>
                            <TableCell colSpan={9} />
                          </TableRow>
                        )}
                      </TableBody>
                    ) : (
                      <TableBody>
                        <TableRow>
                          <TableCell colSpan={9} sx={{ textAlign: 'center' }}>
                            Nessun risultato disponibile
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    )}

                    {/* Table Footer */}
                    {apiCalled && (
                      <TableFooter>
                        <TableRow>
                          <ThemeProvider theme={theme}>
                            <TablePagination
                              sx={{ border: 0 }}
                              rowsPerPageOptions={[]}
                              count={apiResponseData?.total}
                              rowsPerPage={rowsPerPage}
                              page={page}
                              onPageChange={handleChangePage}
                              onRowsPerPageChange={handleChangeRowsPerPage}
                              ActionsComponent={(props) => (
                                <PaginationAPI
                                  {...props}
                                  elements={10}
                                  apiResponseData={apiResponseData}
                                  setApiResponseData={setApiResponseData}
                                  lastPage={apiResponseData.last_page}
                                />
                              )}
                            />
                          </ThemeProvider>
                        </TableRow>
                      </TableFooter>
                    )}
                  </Table>
                </TableContainer>
              </Card>
            </>
          ) : (
            // Show error message
            ErrorUI(errorMessage)
          )
        ) : (
          // Show loading
          LoadingUI()
        )}
      </Container>
    </>
  );
}
